import { forwardRef } from 'react';

import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { abbreviate } from '@utils/strings';

export type AvatarSize = 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl' | 'xxxxl';
export type AvatarColor = 'purple' | 'green' | 'blue' | 'orange' | 'red' | 'theme';

export interface AvatarProps {
  name: string;
  src?: string | null;
  border?: boolean;
  size?: AvatarSize;
  color?: AvatarColor;
  noAbbreviate?: boolean;
  /** Names the avatar for screen readers when its text alone doesn't say who it is. */
  label?: string;
}

export const Avatar = observer<AvatarProps, HTMLDivElement>(
  forwardRef(
    (
      {
        src,
        name,
        size = 'md',
        border = false,
        color = 'green',
        noAbbreviate = false,
        label,
        ...rest
      },
      ref,
    ) => {
      const displayText = noAbbreviate ? name : abbreviate(name) || '?';

      return (
        <AvatarContainer
          ref={ref}
          {...rest}
          data-c="avatar"
          data-size={size}
          data-color={color}
          data-border={border}
          role={label ? 'img' : undefined}
          aria-label={label}
        >
          {src ? <AvatarImage src={src} alt={`avatar for ${name}`} /> : displayText}
        </AvatarContainer>
      );
    },
  ),
);

const AvatarContainer = styled.div({
  flexShrink: 0,
  position: 'relative',
  borderRadius: '50%',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  borderStyle: 'solid',
  borderWidth: 0,
  borderColor: theme.color.border20,
  color: theme.color.white100,
  backgroundClip: 'padding-box',

  '&[data-color="purple"]': {
    backgroundColor: theme.color.elementPurple100,
  },

  '&[data-color="green"]': {
    backgroundColor: theme.color.elementGreen100,
  },

  '&[data-color="blue"]': {
    backgroundColor: theme.color.elementBlue100,
  },

  '&[data-color="orange"]': {
    backgroundColor: theme.color.elementOrange100,
  },

  '&[data-color="red"]': {
    backgroundColor: theme.color.elementRed100,
  },
  '&[data-color="theme"]': {
    backgroundColor: theme.color.backgroundLightDark,
    color: theme.color.textHelper,
  },

  '&[data-size="sm"]': {
    font: theme.font.regular10,
    height: pxToRem(16),
    width: pxToRem(16),
    '&[data-border="true"]': {
      borderWidth: pxToRem(1),
    },
  },

  '&[data-size="md"]': {
    font: theme.font.semibold12,
    height: pxToRem(24),
    width: pxToRem(24),
    '&[data-border="true"]': {
      borderWidth: pxToRem(1),
    },
  },

  '&[data-size="lg"]': {
    font: theme.font.semibold12,
    height: pxToRem(32),
    width: pxToRem(32),
    '&[data-border="true"]': {
      borderWidth: pxToRem(2),
    },
  },

  '&[data-size="xl"]': {
    font: theme.font.semibold14,
    height: pxToRem(40),
    width: pxToRem(40),
    '&[data-border="true"]': {
      borderWidth: pxToRem(2),
    },
  },

  '&[data-size="xxl"]': {
    font: theme.font.heading2,
    height: pxToRem(64),
    width: pxToRem(64),
  },

  '&[data-size="xxxl"]': {
    font: theme.font.heading2,
    height: pxToRem(128),
    width: pxToRem(128),
  },

  '&[data-size="xxxxl"]': {
    font: theme.font.heading2,
    height: pxToRem(170),
    width: pxToRem(170),
  },
});

const AvatarImage = styled.img({
  height: '100%',
  width: '100%',
  objectFit: 'cover',
});
